import React, { useEffect, useMemo, useState } from 'react';
import { Download, Eye, FileText } from 'lucide-react';
import LaborOperationsLayout from '../../../components/labor/LaborOperationsLayout';
import { useAdminAuth } from '../../../contexts/AdminAuthContext';
import {
  fetchLaborAttachmentFile,
  getAdminLaborCases,
  getVisibleAttachments,
} from '../../../features/labor-intake/laborIntakeAdminService';

const previewableExtensions = new Set(['pdf', 'jpg', 'jpeg', 'png']);
const fileSize = (size) => (size ? `${(Number(size) / 1024 / 1024).toFixed(2)} MB` : '-');
const clientName = (client = {}) =>
  client.fullName || [client.firstName, client.fatherName, client.grandfatherName, client.familyName].filter(Boolean).join(' ') || '-';
const extensionOf = (attachment = {}) =>
  (attachment.extension || attachment.fileName || attachment.name || '').split('.').pop()?.toLowerCase() || '';
const hasStoredFile = (attachment = {}) =>
  Boolean(attachment.storageKey || attachment.storage_key || attachment.fileId || attachment.file_id || attachment.fileUrl || attachment.url || attachment.documentUrl || attachment.downloadUrl);

const LaborAttachmentsPage = () => {
  const { user, token } = useAdminAuth();
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getAdminLaborCases(user, token)
      .then(setCases)
      .finally(() => setLoading(false));
  }, [token, user]);

  const attachments = useMemo(() =>
    cases.flatMap((item) =>
      getVisibleAttachments(item).map((attachment) => ({
        ...attachment,
        caseId: item.id,
        referenceNumber: item.referenceNumber,
        clientName: clientName(item.client),
      }))
    ), [cases]);

  const openFile = async (attachment, mode) => {
    const directUrl = attachment.fileUrl || attachment.url || attachment.documentUrl || attachment.downloadUrl;
    if (directUrl) {
      window.open(directUrl, '_blank', 'noopener,noreferrer');
      return;
    }
    const { blob, filename } = await fetchLaborAttachmentFile({
      caseId: attachment.caseId,
      attachmentId: attachment.id,
      mode,
      token,
    });
    const url = URL.createObjectURL(blob);
    if (mode === 'preview') {
      window.open(url, '_blank', 'noopener,noreferrer');
      window.setTimeout(() => URL.revokeObjectURL(url), 60 * 1000);
      return;
    }
    const link = document.createElement('a');
    link.href = url;
    link.download = filename || attachment.fileName || attachment.name || 'attachment';
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  };

  return (
    <LaborOperationsLayout>
      <main className="px-6 pb-12 pt-8 sm:px-10" dir="rtl">
        <header className="mb-8">
          <p className="lo-kicker font-bold">EVIDENCE CENTER</p>
          <h1 className="legal-title mt-3 text-[34px] font-bold text-[#293a50]">مركز المرفقات</h1>
          <p className="mt-3 text-[15px] leading-7 text-[#697483]">عرض مركزي لكل المرفقات المرفوعة وربطها باسم العميل ورقم الطلب.</p>
        </header>

        <section className="lo-surface overflow-hidden rounded-[18px]">
          <div className="flex items-center justify-between border-b border-[#e7e2d9] px-6 py-5">
            <h2 className="text-[18px] font-bold text-[#293a50]">المرفقات المرفوعة</h2>
            <span className="inline-flex items-center gap-2 rounded-full bg-[#f1eee8] px-4 py-2 text-sm font-bold text-[#806f58]">
              <FileText className="h-4 w-4" /> {attachments.length} مرفق
            </span>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full text-right text-[15px]">
              <thead className="bg-[#f4f1eb] text-[13px] font-bold text-[#806f58]">
                <tr>
                  <th className="px-6 py-4">العميل</th>
                  <th className="px-6 py-4">رقم الطلب</th>
                  <th className="px-6 py-4">نوع المرفق</th>
                  <th className="px-6 py-4">اسم الملف</th>
                  <th className="px-6 py-4">الحجم</th>
                  <th className="px-6 py-4">الحالة</th>
                  <th className="px-6 py-4">الإجراء</th>
                </tr>
              </thead>
              <tbody>
                {attachments.map((attachment) => {
                  const available = hasStoredFile(attachment);
                  const previewable = previewableExtensions.has(extensionOf(attachment));
                  return (
                    <tr key={`${attachment.caseId}-${attachment.id}`} className="border-b border-[#ece8e0] transition hover:bg-[#faf8f3]">
                      <td className="px-6 py-5 font-bold text-[#293a50]">{attachment.clientName}</td>
                      <td className="px-6 py-5 text-[#697483]">{attachment.referenceNumber}</td>
                      <td className="px-6 py-5 text-[#697483]">{attachment.typeLabel || attachment.type || '-'}</td>
                      <td className="px-6 py-5 font-bold text-[#293a50]">{attachment.displayName || attachment.fileName || attachment.name || '-'}</td>
                      <td className="px-6 py-5 text-[#697483]">{fileSize(attachment.size)}</td>
                      <td className="px-6 py-5">
                        <span className={`rounded-full px-3 py-1.5 text-xs font-bold ${available ? 'bg-[#eaf3ef] text-[#486e5c]' : 'bg-[#f8eeee] text-[#896264]'}`}>
                          {available ? 'متاح' : 'غير مرفوع فعلياً'}
                        </span>
                      </td>
                      <td className="px-6 py-5">
                        <div className="flex gap-2">
                          <button type="button" disabled={!available || !previewable} onClick={() => openFile(attachment, 'preview')} className="inline-flex h-10 items-center gap-2 rounded-xl bg-[#f1eee8] px-3 text-sm font-bold text-[#293a50] disabled:cursor-not-allowed disabled:opacity-40">
                            <Eye className="h-4 w-4" /> معاينة
                          </button>
                          <button type="button" disabled={!available} onClick={() => openFile(attachment, 'download')} className="inline-flex h-10 items-center gap-2 rounded-xl bg-[#33455d] px-3 text-sm font-bold text-white disabled:cursor-not-allowed disabled:opacity-40">
                            <Download className="h-4 w-4" /> تحميل
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
                {!loading && !attachments.length ? (
                  <tr><td colSpan="7" className="px-6 py-10 text-center text-sm text-[#697483]">لا توجد مرفقات مرفوعة بعد.</td></tr>
                ) : null}
                {loading ? (
                  <tr><td colSpan="7" className="px-6 py-10 text-center text-sm text-[#697483]">جارٍ تحميل المرفقات...</td></tr>
                ) : null}
              </tbody>
            </table>
          </div>
        </section>
      </main>
    </LaborOperationsLayout>
  );
};

export default LaborAttachmentsPage;
